import React, { useState, useEffect, useRef } from 'react';
import './VirtualKeyboard.css';

const VirtualKeyboard = () => {
    const [activeInput, setActiveInput] = useState(null);
    const [isOpen, setIsOpen] = useState(false);
    const [isUppercase, setIsUppercase] = useState(false);
    const [layout, setLayout] = useState('alphanumeric'); // 'alphanumeric' | 'numeric' | 'email'
    const keyboardRef = useRef(null);

    const isDisabledRoute = false;

    const closeKeyboard = () => {
        if (activeInput) {
            activeInput.blur();
        }
        setActiveInput(null);
        setIsOpen(false);
    };

    useEffect(() => {
        if (isDisabledRoute) {
            setIsOpen(false);
            return;
        }
        if (isOpen) {
            document.body.classList.add('keyboard-open');
        } else {
            document.body.classList.remove('keyboard-open');
        }
    }, [isOpen, isDisabledRoute]);



    useEffect(() => {
        if (isDisabledRoute) return;

        const handleFocus = (e) => {
            const target = e.target;
            if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA') {
                if (target.tagName === 'INPUT' && String(target.getAttribute('type') || '').toLowerCase() === 'range') {
                    setActiveInput(null);
                    setIsOpen(false);
                    return;
                }

                // Nuclear option for Windows: Toggle readonly to kill native keyboard trigger
                target.readOnly = true;

                setActiveInput(target);
                setIsOpen(true);

                // Decide layout
                const dataLayout = target.getAttribute('data-layout');
                const mode = target.getAttribute('inputmode');
                const type = target.getAttribute('type');
                const name = target.getAttribute('name') || '';
                const id = target.getAttribute('id') || '';
                const placeholder = target.getAttribute('placeholder') || '';
                const hintText = `${name} ${id} ${placeholder}`.toLowerCase();

                // Enhanced layout detection
                if (dataLayout === 'email' ||
                    type === 'email' ||
                    name.toLowerCase().includes('email') ||
                    id.toLowerCase().includes('email')) {
                    setLayout('email');
                } else if (dataLayout === 'alphanumeric') {
                    setLayout('alphanumeric');
                } else if (dataLayout === 'numeric' ||
                    mode === 'numeric' ||
                    mode === 'tel' ||
                    type === 'number' ||
                    type === 'tel' ||
                    type === 'password' ||
                    name.toLowerCase().includes('mobile') ||
                    name.toLowerCase().includes('otp') ||
                    name.toLowerCase().includes('pin') ||
                    name.toLowerCase().includes('aadhaar') ||
                    name.toLowerCase().includes('account') ||
                    id.toLowerCase().includes('mobile') ||
                    id.toLowerCase().includes('otp') ||
                    id.toLowerCase().includes('pin') ||
                    id.toLowerCase().includes('aadhaar') ||
                    hintText.includes('mpin') ||
                    hintText.includes('pin') ||
                    hintText.includes('passcode') ||
                    hintText.includes('password')) {
                    target.setAttribute('inputmode', 'numeric');
                    setLayout('numeric');
                } else {
                    setLayout('alphanumeric');
                }

                // Restore focusable state after a micro-task to keep cursor but block keyboard
                setTimeout(() => {
                    target.readOnly = false;
                    // Auto-scroll input into view with an offset for the keyboard
                    target.scrollIntoView({ behavior: 'smooth', block: 'center' });
                }, 50);
            }
        };



        const handleBlur = (e) => {
            // Delay closing to allow clicking keys
            setTimeout(() => {
                if (!document.activeElement ||
                    (document.activeElement.tagName !== 'INPUT' &&
                        document.activeElement.tagName !== 'TEXTAREA' &&
                        !keyboardRef.current?.contains(document.activeElement))) {
                    setIsOpen(false);
                }
            }, 150);
        };

        const syncWithActiveElement = () => {
            const activeElement = document.activeElement;
            if (activeElement && (activeElement.tagName === 'INPUT' || activeElement.tagName === 'TEXTAREA')) {
                handleFocus({ target: activeElement });
            }
        };

        // Use document-level focus events so field-level focus changes reliably open the keyboard.
        document.addEventListener('focusin', handleFocus, true);
        document.addEventListener('focusout', handleBlur, true);
        window.addEventListener('focus', syncWithActiveElement, true);

        // Prevent physical keyboard input
        const preventPhysicalInput = (e) => {
            if ((e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') && e.isTrusted) {
                e.preventDefault();
                e.stopPropagation();
                return false;
            }
        };

        // Aggressive Windows/Edge Touch Keyboard suppression
        const suppressSystemKeyboard = () => {
            if (navigator.virtualKeyboard) {
                navigator.virtualKeyboard.hide();
            }
            // Find all inputs and force the manual policy
            document.querySelectorAll('input, textarea').forEach(el => {
                if (!el.getAttribute('virtualkeyboardpolicy')) {
                    el.setAttribute('virtualkeyboardpolicy', 'manual');
                }
            });
        };

        window.addEventListener('keydown', preventPhysicalInput, true);
        window.addEventListener('mousedown', suppressSystemKeyboard, true);
        window.addEventListener('touchstart', suppressSystemKeyboard, true);

        return () => {
            document.removeEventListener('focusin', handleFocus, true);
            document.removeEventListener('focusout', handleBlur, true);
            window.removeEventListener('focus', syncWithActiveElement, true);
            window.removeEventListener('keydown', preventPhysicalInput, true);
            window.removeEventListener('mousedown', suppressSystemKeyboard, true);
            window.removeEventListener('touchstart', suppressSystemKeyboard, true);
        };
    }, [isDisabledRoute]);



    const handleKeyClick = (key) => {
        if (key === 'SHIFT') {
            setIsUppercase(!isUppercase);
            return;
        }

        if (!activeInput) return;

        const start = activeInput.selectionStart ?? activeInput.value.length;
        const end = activeInput.selectionEnd ?? activeInput.value.length;
        const currentValue = activeInput.value;

        let newValue = currentValue;

        if (key === 'BACKSPACE') {
            if (start > 0 || start !== end) {
                const deleteStart = start === end ? start - 1 : start;
                newValue = currentValue.substring(0, deleteStart) + currentValue.substring(end);
                updateInputValue(activeInput, newValue, deleteStart);
            }
        } else if (key === 'CLEAR') {
            newValue = '';
            updateInputValue(activeInput, newValue, 0);
        } else if (key === 'DONE') {
            closeKeyboard();
        } else if (key === 'SPACE') {
            newValue = currentValue.substring(0, start) + ' ' + currentValue.substring(end);
            updateInputValue(activeInput, newValue, start + 1);
        } else {
            const char = isUppercase ? key.toUpperCase() : key.toLowerCase();
            newValue = currentValue.substring(0, start) + char + currentValue.substring(end);
            updateInputValue(activeInput, newValue, start + char.length);
        }
    };

    const updateInputValue = (input, value, newPos) => {
        try {
            // Temporarily lift readOnly if active to ensure sync works on all browsers
            const wasReadOnly = input.readOnly;
            input.readOnly = false;

            // Support both Input and Textarea
            const prototype = input instanceof HTMLTextAreaElement
                ? window.HTMLTextAreaElement.prototype
                : window.HTMLInputElement.prototype;

            const descriptor = Object.getOwnPropertyDescriptor(prototype, "value");
            if (descriptor && descriptor.set) {
                descriptor.set.call(input, value);
            } else {
                input.value = value;
            }

            const event = new Event('input', { bubbles: true });
            input.dispatchEvent(event);

            // Restore readOnly state
            input.readOnly = wasReadOnly;

            // Maintain focus and set selection
            input.focus();
            const supportsSelectionRange =
                typeof input.setSelectionRange === 'function' &&
                !(input instanceof HTMLInputElement && ['number'].includes((input.type || '').toLowerCase()));
            if (supportsSelectionRange) {
                setTimeout(() => {
                    try {
                        input.setSelectionRange(newPos, newPos);
                    } catch (_e) {
                        // Some input types reject setSelectionRange at runtime.
                    }
                }, 0);
            }
        } catch (err) {
            console.error("Keyboard SYNC error:", err);
            // Fallback
            input.value = value;
            const event = new Event('input', { bubbles: true });
            input.dispatchEvent(event);
        }
    };



    if (!isOpen) return null;

    const rows = {
        alphanumeric: [
            ['1', '2', '3', '4', '5', '6', '7', '8', '9', '0'],
            ['q', 'w', 'e', 'r', 't', 'y', 'u', 'i', 'o', 'p'],
            ['a', 's', 'd', 'f', 'g', 'h', 'j', 'k', 'l'],
            ['SHIFT', 'z', 'x', 'c', 'v', 'b', 'n', 'm', 'BACKSPACE'],
            ['SPACE', 'CLEAR', 'DONE']
        ],
        email: [
            ['1', '2', '3', '4', '5', '6', '7', '8', '9', '0'],
            ['q', 'w', 'e', 'r', 't', 'y', 'u', 'i', 'o', 'p'],
            ['a', 's', 'd', 'f', 'g', 'h', 'j', 'k', 'l'],
            ['SHIFT', 'z', 'x', 'c', 'v', 'b', 'n', 'm', 'BACKSPACE'],
            ['@', '.', '_', '-', 'CLEAR'],
            ['DONE', 'SPACE']
        ],
        numeric: [
            ['1', '2', '3'],
            ['4', '5', '6'],
            ['7', '8', '9'],
            ['CLEAR', '0', 'BACKSPACE'],
            ['', 'DONE', '']
        ]
    };

    return (
        <div
            className={`virtual-keyboard-container ${isOpen ? 'open' : ''}`}
            ref={keyboardRef}
            onMouseDown={(e) => e.preventDefault()} // Prevent losing focus from input
        >
            <button type="button" className="keyboard-hide-btn" onClick={closeKeyboard} aria-label="Hide keyboard">×</button>
            <div className="keyboard-inner">
                {layout === 'numeric' ? (
                    <div className="numeric-layout">
                        {rows.numeric.flat().map((key) => (
                            <button
                                key={key}
                                className={`key ${key.length > 1 ? 'special' : ''} ${key === 'DONE' ? 'action' : ''}`}
                                onClick={() => handleKeyClick(key)}
                            >
                                {key === 'BACKSPACE' ? '⌫' : key}
                            </button>
                        ))}
                    </div>
                ) : (
                    rows[layout].map((row, i) => (
                        <div key={i} className="keyboard-row">
                            {row.map((key) => {
                                let label = key;
                                if (key.length === 1 && key.match(/[a-z]/i)) {
                                    label = isUppercase ? key.toUpperCase() : key.toLowerCase();
                                }
                                if (key === 'BACKSPACE') label = '⌫';
                                if (key === 'SHIFT') label = isUppercase ? '⬆' : '⬇';

                                return (
                                    <button
                                        key={key}
                                        className={`key ${key.length > 1 ? 'special' : ''} ${key === 'DONE' ? 'action' : ''} ${key === 'SPACE' ? 'space' : ''} ${key === 'CLEAR' ? 'compact' : ''} ${key === 'SHIFT' && isUppercase ? 'active' : ''}`}
                                        onClick={() => handleKeyClick(key)}
                                    >
                                        {label}
                                    </button>
                                );
                            })}
                        </div>
                    ))
                )}
            </div>
        </div>
    );
};

export default VirtualKeyboard;
